import { FilterState, FilterStateService } from './filter-state.service';
import { Component, OnInit, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    BehaviorSubject,
    combineLatest, mergeWith,
    Observable,
    of,
    shareReplay,
    Subject,
    switchMap,
    takeUntil,
} from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { Aufgabe } from 'src/app/domain/model/aufgabe';
import { UflaUser } from 'src/app/service/auth/ufla-user';
import { AufgabeDialogService } from '../aufgabe-dialog.service';
import { Zuweisung } from '../../domain/model/zuweisung';
import { AufgabeService } from '../../service/aufgabe/aufgabe.service';
import { AuthenticationService } from '../../service/auth/authentication.service';
import { VorgangsTyp } from '../../domain/model/vorgangsTyp';
import { UntypedFormControl } from '@angular/forms';
import { StatusTyp } from '../../domain/model/statusTyp';

@Component({
    selector: 'ufla-aufgaben-list',
    templateUrl: './aufgaben-list.component.html',
    styleUrls: [
        './aufgaben-list.component.scss',
    ],
    standalone: false,
})
export class AufgabenListComponent implements OnInit, OnDestroy {
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private aufgabeService = inject(AufgabeService);
    private aufgabeDialogService = inject(AufgabeDialogService);
    private authenticationService = inject(AuthenticationService);
    private filterStateService = inject(FilterStateService);

    uflaUser: UflaUser;
    aufgaben$: Observable<Aufgabe[]>;
    erhalten$: Observable<Zuweisung[]>;

    aufgabenFiltered$: Observable<Aufgabe[]>;
    erhaltenFiltered$: Observable<Zuweisung[]>;
    years$: Observable<string[]>;

    yearFilterFormControl = new UntypedFormControl(null);
    typeFilterFormControl = new UntypedFormControl([]);
    statusFilterFormControl = new UntypedFormControl([]);
    typesFilter: Array<VorgangsTyp> = Object.values(VorgangsTyp);
    statusFilter: Array<StatusTyp> = Object.values(StatusTyp);
    readonly allYears = 'ALL';

    private reload$: Subject<void> = new BehaviorSubject<void>(null);
    private refilter$: Subject<void> = new Subject<void>();
    private onDestroy$: Subject<void> = new Subject<void>();

    constructor() {
        this.uflaUser = this.route.snapshot.data.profile;

        this.aufgaben$ =
      this.reload$
          .pipe(
              switchMap(() => this.aufgabeService.listAufgabeByOrganisation(this.uflaUser.organisationsId)),
              shareReplay(1),
          );
        this.erhalten$ =
      this.reload$
          .pipe(
              switchMap(() => this.aufgabeService.listZuweisungByOrganisation(this.uflaUser.organisationsId)),
              shareReplay(1),
          );
    }

    ngOnInit(): void {
        this.initFilterObservable();

        this.aufgabeDialogService.observeAufgabeChanged()
            .pipe(
                takeUntil(this.onDestroy$),
            )
            .subscribe(() => this.reload$.next());

        this.filterStateService.observeState$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(fs => {
                this.yearFilterFormControl.setValue(fs.year || new Date().getFullYear().toString(), { emitEvent: false });
                this.typeFilterFormControl.setValue(fs.types || Object.values(VorgangsTyp), { emitEvent: false });
                this.statusFilterFormControl.setValue(fs.status || Object.values(StatusTyp), { emitEvent: false });

                this.refilter$.next();
            });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    private initFilterObservable(): void {
        this.aufgabenFiltered$ =
      this.refilter$.pipe(
          mergeMap(() => this.aufgaben$),
          map(aufgaben => {
              return aufgaben
                  .filter(aufgabe => this.typesFilter ? this.typeFilterFormControl.value.includes(aufgabe.type) : true)
                  .filter(aufgabe => this.yearFilterFormControl.value ?
                      this.allYears === this.yearFilterFormControl.value || aufgabe.frist.startsWith(this.yearFilterFormControl.value) :
                      true)
                  .filter(aufgabe => this.statusFilter ? this.statusFilterFormControl.value
                      .includes((this.aufgabeService.alleZuweisungenGelesen(aufgabe) ? 'erledigt/gelesen' : 'offen')) : true);
          }),
      );

        this.erhaltenFiltered$ =
      this.refilter$.pipe(
          mergeMap(() => this.erhalten$),
          map(zuweisungen => {
              return zuweisungen
                  .filter(zuweisung => this.typesFilter ? this.typeFilterFormControl.value.includes(zuweisung.aufgabe.type) : true)
                  .filter(zuweisung => this.yearFilterFormControl.value ?
                      this.allYears === this.yearFilterFormControl.value || zuweisung.aufgabe.frist.startsWith(this.yearFilterFormControl.value) :
                      true)
                  .filter(zuweisung => this.statusFilter ? this.statusFilterFormControl.value
                      .includes((zuweisung.gelesen ? 'erledigt/gelesen' : 'offen')) : true);
          }),
      );

        this.years$ = combineLatest([
            this.aufgaben$.pipe(
                map(aufgaben => aufgaben.map(a => a.frist.substring(0, 4))),
            ),
            this.erhalten$.pipe(
                map(zuweisungen => zuweisungen.map(z => z.aufgabe.frist.substring(0, 4))),
            ),
            of([
                new Date().getFullYear().toString(),
            ]),
        ]).pipe(
            map(([
                y1,
                y2,
                y3,
            ]) => {
                return [
                    ...new Set([
                        ...y1,
                        ...y2,
                        ...y3,
                    ]),
                ]
                    .sort((a, b) => a.localeCompare(b));
            }),
            tap(() => this.refilter$.next()),
        );

        this.yearFilterFormControl.valueChanges.pipe(
            mergeWith(
                this.typeFilterFormControl.valueChanges,
                this.statusFilterFormControl.valueChanges,
            ),
            takeUntil(this.onDestroy$),
        ).subscribe(() => {
            this.filterStateService.storeState(
                new FilterState(
                    this.yearFilterFormControl.value,
                    this.typeFilterFormControl.value,
                    this.statusFilterFormControl.value,
                ),
            );
        });
    }

    public openDetails(id: any) {
        this.router.navigate([
            'dashboard',
            'details',
            id,
        ]);
    }

    public openZuweisung(id: any) {
        this.router.navigate([
            'dashboard',
            'zuweisung',
            id,
        ]);
    }

    public showCreated(): boolean {
        return this.authenticationService.isAdmin(this.uflaUser)
      || this.authenticationService.isVnb(this.uflaUser)
      || this.authenticationService.isUenb(this.uflaUser);
    }

    public resetFilter(): void {
        this.filterStateService.storeState();
    }
}
